import { Injectable, Inject, PLATFORM_ID, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ApiBaseService } from '../api/base/api-base.service';
import { isPlatformBrowser } from '@angular/common';
import { User } from '../models/auth/user.model';
import { AuthResponse } from '../models/auth/auth-response.model';
import { ApiLoginCredentials } from '../../features/auth/models/login-form.model';
import { jwtDecode } from 'jwt-decode';
import { RegisterCredentials } from '@features/auth/models/register-form.model';
import { ResponseWithSingleData } from 'src/app/shared/interfaces/response.interface';
import { SettingsService } from './settings.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends ApiBaseService {
  private currentUserSignal = signal<User | null>(null);
  private accessTokenSignal = signal<string | null>(null);

  readonly user = computed(() => this.currentUserSignal());
  readonly token = computed(() => this.accessTokenSignal());
  readonly isAuthenticated = computed(() => {
    const token = this.accessTokenSignal();
    return !!token;
  });
  readonly roles = computed(() => {
    const user = this.currentUserSignal();
    // Si el usuario tiene employee con profile, usar el nombre del profile como rol
    if (user?.employee?.profile?.name) {
      const role = user.employee.profile.name.toLowerCase().trim();
      return [role];
    }
    if (user?.employee?.profile_name) {
      const role = user.employee.profile_name.toLowerCase().trim();
      return [role];
    }
    return user?.roles || [];
  });
  readonly employeeId = computed(() => this.currentUserSignal()?.employee_id || null);
  readonly employee = computed(() => this.currentUserSignal()?.employee || null);

  /**
   * Permisos granulares del usuario, devueltos por el endpoint /me.
   * Array de slugs como 'orders.create', 'sales.view_all', etc.
   */
  readonly permissions = computed(() => this.currentUserSignal()?.permissions ?? []);

  private tokenKey = 'auth_token';
  private userKey = 'current_user';
  private isBrowser: boolean;
  private settingsService = inject(SettingsService);

  constructor(
    protected override http: HttpClient,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    super(http);
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.loadStoredUserData();
  }

  login(credentials: ApiLoginCredentials, rememberMe: boolean = false): Observable<ResponseWithSingleData<AuthResponse>> {
    return this.post<ResponseWithSingleData<AuthResponse>>('auth/login', credentials).pipe(
      tap(response => {
        this.storeAuthData(response.data, rememberMe);
      }),
      map(response => response),
      catchError(error => {
        console.error('Error durante el login', error);
        return throwError(() => new Error('Credenciales inválidas'));
      })
    );
  }

  register(credentials: RegisterCredentials) {
    return this.post<AuthResponse>('auth/register', credentials).pipe(
      tap(response => {
        this.storeAuthData(response, false);
      }),
      map(response => response.user),
      catchError(error => {
        console.error('Error durante el registro', error);
        return throwError(() => new Error('Error al registrar el usuario'));
      })
    );
  }

  logout(): void {
    // Limpiar settings antes del logout
    this.settingsService.onLogout();

    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    localStorage.removeItem('remember_auth');
    sessionStorage.removeItem(this.tokenKey);
    sessionStorage.removeItem(this.userKey);

    this.currentUserSignal.set(null);
    this.accessTokenSignal.set(null);

    this.router.navigateByUrl('/login');
  }

  hasRole(role: string): boolean {
    return this.roles().includes(role);
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.some(role => this.hasRole(role));
  }

  /**
   * Verifica si el usuario tiene un permiso granular específico.
   * @param slug El slug del permiso, e.g. 'orders.create'
   */
  hasPermission(slug: string): boolean {
    return this.permissions().includes(slug);
  }

  private storeAuthData(response: AuthResponse, rememberMe: boolean): void {
    this.currentUserSignal.set(response.user);
    this.accessTokenSignal.set(response.token);

    if (this.isBrowser) {
      localStorage.setItem('remember_auth', rememberMe.toString());

      if (rememberMe) {
        localStorage.setItem(this.tokenKey, response.token);
        localStorage.setItem(this.userKey, JSON.stringify(response.user));
      } else {
        sessionStorage.setItem(this.tokenKey, response.token);
        sessionStorage.setItem(this.userKey, JSON.stringify(response.user));
      }

      // Cargar settings después del login
      this.settingsService.onLogin();
    }
  }

  private loadStoredUserData(): void {
    if (this.isBrowser) {
      let token = localStorage.getItem(this.tokenKey);
      let storedUser = localStorage.getItem(this.userKey);

      if (!token || !storedUser) {
        token = sessionStorage.getItem(this.tokenKey);
        storedUser = sessionStorage.getItem(this.userKey);
      }

      if (token && storedUser) {
        try {
          const user = JSON.parse(storedUser);
          this.currentUserSignal.set(user);
          this.accessTokenSignal.set(token);
        } catch (e) {
          console.error('Error parsing stored user data', e);
          this.logout();
        }
      }
    }
  }

  /**
   * Updates the current user in memory and storage.
   * Used when profile is updated.
   */
  updateCurrentUser(user: User): void {
    this.currentUserSignal.set(user);

    if (this.isBrowser) {
      const userJson = JSON.stringify(user);

      // Check where the user data is stored and update accordingly
      if (localStorage.getItem(this.userKey)) {
        localStorage.setItem(this.userKey, userJson);
      } else if (sessionStorage.getItem(this.userKey)) {
        sessionStorage.setItem(this.userKey, userJson);
      }
    }
  }
}